/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Helpers
import _forEach from 'lodash/forEach';
import Page from '../../lib/page';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import {
  ECM_DOC_ID_KEY,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
} from '../constants/ecmFPTITagsKeys';

const duration = 400;

class Article extends Component {
  static getMarkup(articleContent) {
    return { __html: articleContent };
  }

  // To form faq link for by FAQ id of solutionLink in the content
  static handleFAQClick(event) {
    const clickTarget = event.target;
    const id = clickTarget.id;

    // To check the click content is solutionLink
    if (id && clickTarget.name === 'solutionLink') {
      event.preventDefault();
      const target = clickTarget.target || '_self';
      // TODO: add article title into the getArticleLink function
      window.open(Page.getArticleLink(id), target);
    }
  }

  static handleKeyPress(event) {
    if (event.which === 13) {
      Article.handleFAQClick();
    }
  }

  // add the FPTI events for Play and Pause clicks for the videos
  static addFPTIsForVideoPlayPause(videoElement, articleId, fptiDocId, fptiComponentIds) {
    videoElement.addEventListener('play', () => {
      // Add FPTI event for when video would be played
      trackLink({
        id: 'articlePageVideo',
        link: 'article-page-video-played',
        hpid: articleId,
        help_doc_id: fptiDocId,
        help_component_ids: fptiComponentIds,
      });
    });

    videoElement.addEventListener('pause', () => {
      // Add FPTI event for when video would be paused
      trackLink({
        id: 'articlePageVideo',
        link: 'article-page-video-paused',
        hpid: articleId,
        help_doc_id: fptiDocId,
        help_component_ids: fptiComponentIds,
      });
    });
  }

  constructor(props) {
    super(props);
    this.contentElement = null;
    this.state = { articleVideosId: [] }; // array to store the ids for videos in the article
  }

  componentDidMount() {
    this.parseArticleContent();
  }

  handleLinkClick(e, link) {
    const articleId = this.props.article.articleId;
    const href = link.getAttribute('href');

    if (!href) {
      e.preventDefault();
      trackLink({ id: this.props.pageName, link: `smarthelp-related-article|${articleId}|broken-link-clicked` });
    } else {
      trackLink({ id: this.props.pageName, link: `smarthelp-related-article|${articleId}|link-clicked|${href}` });
      if (Page.isAnchorLink(href) && !Page.isMPPEnable()) {
        setTimeout(() => Page.scrollDown(), 20);
      }
    }
  }

  // Remove img replacement to div due to icon breakline issue
  parseArticleContent() {
    // Adding setTimeout on top as tag element is not picked immediately safari and IE versions
    setTimeout(() => {
      if (!this.contentElement) {
        return;
      }

      // Link click Tracking in Article Component
      const contentLinks = this.contentElement.querySelectorAll('a[href]');
      Array.from(contentLinks).forEach((link) => {
        link.addEventListener('click', (e) => {
          const clickTarget = e.target;
          const { id } = clickTarget;

          // To check if the click content is solutionLink
          if (id && clickTarget.name === 'solutionLink') {
            // make the faqId the href for fpti usage
            link.href = id;
          }

          this.handleLinkClick(e, link);
        });
      });

      // warp image in the article content
      let imageTags = this.contentElement.getElementsByTagName('img');
      imageTags = Array.prototype.slice.call(imageTags);
      imageTags.forEach((tag) => {
        const divEl = document.createElement('img');
        divEl.setAttribute('src', tag.src);
        divEl.style.height = `${tag.height}px`;
        divEl.style.maxWidth = `${tag.width}px`;
        divEl.setAttribute('alt', tag.alt);
        divEl.className = 'okm-image';
        tag.parentNode.replaceChild(divEl, tag);
      });

      // rewarp Youtube video content
      let iframeTags = this.contentElement.getElementsByTagName('iframe');
      iframeTags = Array.prototype.slice.call(iframeTags);
      iframeTags.forEach((tag) => {
        const videoEl = tag;
        videoEl.className = 'okm-paypal-iframe';
        videoEl.setAttribute('src', tag.src && tag.src.replace('youtube.com', 'youtube-nocookie.com'));
        tag.parentNode.replaceChild(videoEl, tag);
      });
    });

    // get the only video tags which do not exist in the state video ids array (articleVideosId)
    let videoElementsByTagName = Array.prototype.slice.call(document.getElementsByTagName('video'));
    videoElementsByTagName = videoElementsByTagName.filter((video) =>
      this.state.articleVideosId.every((videoId) => videoId !== video.id),
    );

    // If the video exists in article
    if (videoElementsByTagName.length) {
      const processedVideoIds = [];
      const articleId = (this.props.article && this.props.article.articleId) || '';

      // get the ECM API tags from article data
      const fptiDocId = (this.props.article && this.props.article[ECM_DOC_ID_KEY]) || ECM_DOC_ID_DEFAULT_VALUE;
      const fptiComponentIds =
        (this.props.article && this.props.article[ECM_COMPONENT_IDS_KEY]) || ECM_COMPONENT_IDS_DEFAULT_VALUE;

      // fire FPTI for every video loaded on the article page
      _forEach(videoElementsByTagName, (videoElement) => {
        // store the video ids to array to process the videos only once
        processedVideoIds.push(videoElement.id);
        // need to provide some buffer time in-between the FPTIs otherwise it's not able to detect all the videos on article page
        setTimeout(() => {
          trackPage({
            id: 'articlePageVideo',
            link: 'video-displayed',
            hpid: articleId,
            help_doc_id: fptiDocId,
            help_component_ids: fptiComponentIds,
          });
          Article.addFPTIsForVideoPlayPause(videoElement, articleId, fptiDocId, fptiComponentIds);
        }, duration);
      });
      this.setState({ articleVideosId: [...this.state.articleVideosId, ...processedVideoIds] });
    }
  }

  render() {
    const { article } = this.props;
    const articleContent = article && article.articleContent;
    const articleTitle = article && article.articleTitle;
    const tabAttribs = {
      className: this.props.className,
      role: 'tabpanel',
      tabIndex: '0',
    };

    return (
      <div {...tabAttribs}>
        <p className="title">{articleTitle}</p>
        <div className="contents" role="article">
          <p // eslint-disable-line jsx-a11y/no-static-element-interactions
            className="okm-content"
            dangerouslySetInnerHTML={Article.getMarkup(articleContent)} // eslint-disable-line react/no-danger
            ref={(el) => {
              this.contentElement = el;
            }}
            onClick={Article.handleFAQClick}
            onKeyPress={Article.handleKeyPress}
          />
        </div>
      </div>
    );
  }
}

Article.defaultProps = {
  article: undefined,
  className: 'article-component',
  pageName: 'contactUsPage',
};

Article.propTypes = {
  article: PropTypes.shape({
    articleId: PropTypes.string,
    articleTitle: PropTypes.string,
    topicId: PropTypes.string,
    articleContent: PropTypes.string,
  }),
  className: PropTypes.string,
  pageName: PropTypes.string,
};

export default Article;
